import React, { useEffect, useState } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { checkOrganizationNameExists } from '../../../services/organizationService';
import { organizationNameField } from '../../../validation/organization';
import TextField from 'apollo-react/components/TextField';
import Grid from 'apollo-react/components/Grid';
import Typography from '../../common/StyledTypography';
import ButtonCommon from '../../common/Button';
import ProfileFieldDisplay from '../../common/Profile/sections/ProfileFieldDisplay';
import ProfileEditActions from '../../common/Profile/sections/ProfileEditActions';
import { convertUTCToLocal } from '../../utils/dateUtils';
import profileStyles from '../../common/Profile/Profile.module.css';
import styles from './details.module.css';

const OrganizationDetails = ({
  showEditButton = true,
  currentOrganization = null,
}) => {
  const { id } = useParams();
  const [organization, setOrganization] = useState(currentOrganization);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    name: currentOrganization?.name || '',
  });
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [t] = useTranslation();

  const fetchOrganization = async () => {
    try {
      const response = await axiosInstance.get(`/api/v1/organizations/${id}`, {
        withCredentials: true,
      });
      const data = response.data.data;
      setOrganization(data);
      setEditData({ name: data.name || '' });
    } catch (err) {
      const errorMsg =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        t('error_fetching_organization') ||
        'Error fetching organization';
      toast.error(errorMsg);
    }
  };

  useEffect(() => {
    if (!currentOrganization) {
      fetchOrganization();
    }
  }, [id, currentOrganization]);

  const handleDiscard = () => {
    setEditData({ name: organization.name || '' });
    setNameError('');
    setIsEditing(false);
  };

  const handleChange = (field, value) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
    if (field === 'name') setNameError('');
  };

  const validateName = (name) => {
    const result = organizationNameField(t).safeParse(name);
    if (result.success) return '';
    return result.error.issues[0]?.message || t('organization_name_required');
  };

  const handleSave = async () => {
    const validationError = validateName(editData.name);
    if (validationError) {
      setNameError(validationError);
      return;
    }

    setSaving(true);
    try {
      const nameExists = await checkOrganizationNameExists(editData.name, id);
      if (nameExists) {
        setNameError(
          t('organization_name_already_exists') ||
            'Organization with this name already exists'
        );
        setSaving(false);
        return;
      }

      await axiosInstance.put(`/api/v1/organizations/${id}`, {
        name: editData.name.trim(),
      });
      toast.success(
        t('organization_updated_successfully') ||
          'Organization updated successfully'
      );
      setNameError('');
      setIsEditing(false);
      fetchOrganization();
    } catch (error) {
      const errorMsg =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        t('error_updating_organization') ||
        'Error updating organization';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const hasNameChanged =
    editData.name?.trim() !== (organization?.name?.trim() || '');

  if (!organization) return null;

  const typeBadge = (
    <span
      className={`${styles.readOnlyBadge} ${
        organization.type === 'internal'
          ? styles.badgeInternal
          : styles.badgeExternal
      }`}
    >
      {organization.type?.charAt(0).toUpperCase() + organization.type?.slice(1)}
    </span>
  );

  if (isEditing) {
    return (
      <div className="container mt-4 col-md-10">
        <div className={profileStyles.card}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('name') || 'Name'}
                value={editData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                fullWidth
                error={!!nameError}
                helperText={nameError}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('type') || 'Type'}
                value={
                  organization.type?.charAt(0).toUpperCase() +
                  organization.type?.slice(1)
                }
                disabled
                fullWidth
              />
            </Grid>
            {organization.type === 'external' && organization.category && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('category') || 'Category'}
                  value={organization.category.name || organization.category}
                  disabled
                  fullWidth
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('organization_code') || 'Organization Code'}
                value={organization.code || '-'}
                disabled
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label={t('created_at') || 'Created At'}
                value={convertUTCToLocal(organization.createdAt)}
                disabled
                fullWidth
              />
            </Grid>
            {organization.updatedAt && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('updated_at') || 'Updated At'}
                  value={convertUTCToLocal(organization.updatedAt)}
                  disabled
                  fullWidth
                />
              </Grid>
            )}
          </Grid>
          <ProfileEditActions
            onDiscard={handleDiscard}
            onSave={handleSave}
            saveText={saving ? t('saving') || 'Saving...' : t('Save') || 'Save'}
            saveDisabled={!hasNameChanged || saving}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4 col-md-10">
      <div className={profileStyles.card}>
        <ProfileFieldDisplay
          label={t('name') || 'Name'}
          value={organization.name}
        />
        <ProfileFieldDisplay label={t('type') || 'Type'} value={typeBadge} />
        {organization.type === 'external' && organization.category && (
          <ProfileFieldDisplay
            label={t('category') || 'Category'}
            value={organization.category.name || organization.category}
          />
        )}
        <ProfileFieldDisplay
          label={t('organization_code') || 'Organization Code'}
          value={organization.code}
        />
        <ProfileFieldDisplay
          label={t('created_at') || 'Created At'}
          value={convertUTCToLocal(organization.createdAt)}
        />
        {organization.updatedAt && (
          <ProfileFieldDisplay
            label={t('updated_at') || 'Updated At'}
            value={convertUTCToLocal(organization.updatedAt)}
          />
        )}
        {showEditButton && (
          <div className={profileStyles.actionsRight}>
            <ButtonCommon
              color="primary"
              className={profileStyles.btnSized}
              onClick={() => setIsEditing(true)}
              text={t('edit')}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OrganizationDetails;
